"use client"

import { useFormatter } from "@/hooks/use-formatter"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { IconCircleCheckFilled, IconClipboardList } from "@tabler/icons-react"

import { Eyebrow } from "@/components/ui/eyebrow"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Chip,
  EmptyState,
  KCard,
  SectionHead,
} from "@/components/leads/kartele/primitives"
import { getQuotesForLead } from "@/lib/quotes-api"
import { resolveQuoteTranslation } from "@/lib/utils/quote-i18n"
import type { Quote, QuoteItem } from "@/types"

interface LeadTreatmentPlanProps {
  leadId: string
}

/**
 * LeadTreatmentPlan - read-only projection of a lead's accepted quote.
 *
 * Price fields are deliberately NOT rendered (DOC-12). The viewer uses an
 * explicit FIELD ALLOWLIST of {name, description, quantity, arch} so that
 * adding a new price field to QuoteItemResource on the backend can never
 * leak it into the doctor view. See 40-RESEARCH.md Pitfall 6 for rationale.
 * Quote-level metadata in the header (quote_number, accepted_at) is
 * identity/lifecycle data, not pricing.
 *
 * If no accepted quote exists, renders an empty placeholder (DOC-13).
 */
export function LeadTreatmentPlan({ leadId }: LeadTreatmentPlanProps) {
  const clinicFormat = useFormatter()
  const t = useTranslations("leadDetail")
  const [acceptedQuote, setAcceptedQuote] = useState<Quote | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true)
    getQuotesForLead(leadId)
      .then((quotes) => {
        if (cancelled) return
        // Newest accepted wins — a revised quote leaves two accepted rows.
        const accepted =
          quotes
            .filter((q) => q.status === "accepted")
            .sort((a, b) => (b.accepted_at ?? "").localeCompare(a.accepted_at ?? ""))[0] ?? null
        setAcceptedQuote(accepted)
      })
      .catch((error) => {
        if (cancelled) return
        console.error("Failed to load treatment plan", error)
        toast.error(t("treatmentPlan.loadFailed"))
        setAcceptedQuote(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [leadId, t])

  if (isLoading) {
    return (
      <div className="p-5">
        <KCard>
          <div className="border-b border-border px-[22px] py-3.5">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="mt-1.5 h-3 w-32" />
          </div>
          <div className="space-y-3 px-[18px] py-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </KCard>
      </div>
    )
  }

  if (!acceptedQuote) {
    return (
      <TreatmentPlanEmpty
        title={t("treatmentPlan.noAcceptedTitle")}
        hint={t("treatmentPlan.noAcceptedBody")}
      />
    )
  }

  const items: QuoteItem[] = acceptedQuote.items ?? []
  const upperArchItems = items.filter((i) => i.arch === "upper")
  const lowerArchItems = items.filter((i) => i.arch === "lower")
  const otherItems = items.filter((i) => i.arch !== "upper" && i.arch !== "lower")

  if (items.length === 0) {
    return (
      <TreatmentPlanEmpty
        title={t("treatmentPlan.noLineItemsTitle")}
        hint={t("treatmentPlan.noLineItemsBody")}
      />
    )
  }

  const groups: Array<{ key: string; title: string; items: QuoteItem[] }> = [
    { key: "upper", title: t("treatmentPlan.upperArch"), items: upperArchItems },
    { key: "lower", title: t("treatmentPlan.lowerArch"), items: lowerArchItems },
    { key: "other", title: t("treatmentPlan.procedures"), items: otherItems },
  ].filter((g) => g.items.length > 0)

  return (
    <div className="p-5">
      <KCard>
        <SectionHead
          title={t("treatmentPlan.heading")}
          sub={
            <span title={t("treatmentPlan.headingHint")}>
              {acceptedQuote.quote_number}
              {" · "}
              {t("treatmentPlan.procedureCount", { count: items.length })}
            </span>
          }
          action={
            acceptedQuote.accepted_at ? (
              <Chip tone="success">
                <IconCircleCheckFilled className="size-3.5" aria-hidden />
                {t("treatmentPlan.acceptedOn", {
                  date: clinicFormat.dateTime(new Date(acceptedQuote.accepted_at), { year: "numeric", month: "short", day: "numeric" }),
                })}
              </Chip>
            ) : (
              <Chip tone="success">
                <IconCircleCheckFilled className="size-3.5" aria-hidden />
                {t("treatmentPlan.acceptedChip")}
              </Chip>
            )
          }
        />

        {groups.map((group, groupIndex) => (
          <section key={group.key} aria-label={group.title}>
            <Eyebrow
              className={
                groupIndex === 0
                  ? "px-[18px] pb-1.5 pt-3"
                  : "border-t border-border px-[18px] pb-1.5 pt-3"
              }
            >
              {group.title}
            </Eyebrow>
            <div className="divide-y divide-border/60">
              {group.items.map((item) => {
                // EXPLICIT ALLOWLIST - only these four fields are rendered.
                // Adding a price field here is a code-review red flag.
                const name = resolveQuoteTranslation(item.name)
                const description = resolveQuoteTranslation(item.description)
                const quantity = item.quantity
                const arch = item.arch

                return (
                  <div
                    key={item.id}
                    className="flex items-baseline gap-3 px-[18px] py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <p className="truncate text-[13px] font-medium text-foreground">
                          {name}
                        </p>
                        {/* Arch chip only outside the arch groups — inside
                            them the Eyebrow already states the arch. */}
                        {group.key === "other" && arch && (
                          <Chip tone="soft" className="capitalize">
                            {arch}
                          </Chip>
                        )}
                      </div>
                      {description && (
                        <p className="mt-0.5 truncate text-[11.5px] text-text-dim">
                          {description}
                        </p>
                      )}
                    </div>
                    <span
                      className="shrink-0 text-[12px] tabular-nums text-muted-foreground"
                      title={`${t("treatmentPlan.qty")} ${quantity}`}
                    >
                      ×{quantity}
                    </span>
                  </div>
                )
              })}
            </div>
          </section>
        ))}
      </KCard>
    </div>
  )
}

function TreatmentPlanEmpty({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="p-5">
      <KCard>
        <div className="p-[18px]">
          <EmptyState
            icon={<IconClipboardList className="size-5" />}
            title={title}
            hint={hint}
          />
        </div>
      </KCard>
    </div>
  )
}
